import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Activity } from 'lucide-react';
import { StatTile, TrendBadge } from '../simulation/ScoreTrendChart';
import { formatDateTime } from './eeocUtils';

const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');

const TrendTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
            <p className="font-semibold">{p.module_title}</p>
            <p className="mt-1">
                <span className="text-base font-bold tabular-nums">{p.score}</span> / 100 ·{' '}
                <span className={p.passed ? 'text-emerald-600' : 'text-red-600'}>{p.passed ? 'PASS' : 'FAIL'}</span>
            </p>
            <p className="text-muted-foreground">{formatDateTime(p.date)} · pass mark {p.passing_score}</p>
        </div>
    );
};

const renderDot = ({ cx, cy, payload, index }) => (
    <circle key={`d-${index}`} cx={cx} cy={cy} r={5} fill={payload.passed ? '#059669' : '#dc2626'} stroke="hsl(var(--background))" strokeWidth={2} />
);

export const ComplianceTrend = ({ points, summary, passingScore, testId = 'eeoc-trend' }) => {
    if (!points?.length) {
        return (
            <div className="flex h-[150px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed text-center" data-testid={`${testId}-empty`}>
                <Activity className="h-6 w-6 text-muted-foreground" />
                <p className="max-w-xs text-sm text-muted-foreground">Complete a session to start tracking compliance over time.</p>
            </div>
        );
    }
    const data = points.map((p, i) => ({ ...p, index: i + 1 }));
    const tick = (i) => {
        const label = shortDate(data[i - 1]?.date);
        return i > 1 && label === shortDate(data[i - 2]?.date) ? '' : label;
    };
    return (
        <div className="grid gap-4 lg:grid-cols-[220px_1fr]" data-testid={testId}>
            <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                    <StatTile label="Latest" value={summary.latest} testId={`${testId}-latest`} />
                    <StatTile label="Best" value={summary.best} testId={`${testId}-best`} />
                    <StatTile label="Average" value={summary.average} testId={`${testId}-average`} />
                    <StatTile label="Passed" value={`${summary.passed_sessions}/${summary.count}`} testId={`${testId}-passed`} />
                </div>
                <TrendBadge summary={summary} />
            </div>
            <div className="h-[200px] w-full" data-testid={`${testId}-chart`}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 10, right: 48, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="index" tickLine={false} axisLine={false} fontSize={11} tickFormatter={tick} interval={data.length <= 12 ? 0 : 'preserveStartEnd'} />
                        <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickLine={false} axisLine={false} fontSize={11} />
                        <ReferenceLine y={passingScore} stroke="#0284c7" strokeDasharray="4 4" label={{ value: `Pass ${passingScore}`, position: 'right', fontSize: 10, fill: '#0284c7' }} />
                        <Tooltip content={<TrendTooltip />} cursor={{ stroke: 'hsl(var(--border))' }} />
                        <Line type="monotone" dataKey="score" stroke="#0284c7" strokeWidth={2.5} dot={renderDot} activeDot={{ r: 7 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
